import { Injectable, inject } from '@angular/core';

import { EventCarouselFixtureResponse } from '@cds/betting-offer/domain-specific/event-carousel';
import { EventCarouselFixtureRequest, FixtureRequest, FixtureState, ParticipantMapping, ScoreboardMode, SortByCriteria } from '@cds/query-objects';
import { FixtureFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventModel, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { EpcotConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { EMPTY, Observable, expand, map, tap } from 'rxjs';

import { EventCarouselItem } from '../../event-carousel/event-carousel.model';
import { LeftNavType } from '../../event-details-common/event-details-common.models';
import { EventCarouselApi } from './event-carousel-api.service';

const PAGE_SIZE = 10;
@Injectable({ providedIn: 'root' })
export class SheetViewCarouselDataService {
    callFixtures = true;

    private eventCarouselApi = inject(EventCarouselApi);
    private fixtureFactory = inject(FixtureFactory);
    private epcotConfig = inject(EpcotConfig);

    private skip = 0;

    getFixtures(event: EventModel, optionGroup: EventOptionGroup, dynamicOfferCategory?: string): Observable<EventCarouselItem[] | undefined> {
        return this.getFixturesdata(event, optionGroup, PAGE_SIZE, dynamicOfferCategory).pipe(
            expand((_) => (this.callFixtures ? this.getFixturesdata(event, optionGroup, PAGE_SIZE, dynamicOfferCategory) : EMPTY)),
        );
    }

    private getFixturesdata(
        event: EventModel,
        optionGroup: EventOptionGroup,
        pageSize?: number,
        dynamicOfferCategory?: string,
    ): Observable<EventCarouselItem[] | undefined> {
        const fixtureReq: EventCarouselFixtureRequest = {
            ...this.getCommonFixtureRequest(event, undefined, undefined, optionGroup.detailedGrouping.marketGroupItemId, dynamicOfferCategory),
            includeFixturesWithSplit: true,
            priorityFixtureId: event.id,
            sortBy: SortByCriteria.FixtureStage,
            skip: this.skip,
            take: pageSize,
        };

        return this.eventCarouselApi.getFixtureList(fixtureReq).pipe(
            tap((res) => res && this.setParams(res)),
            map((data) => data && this.mapResponse(data)),
        );
    }

    private getCommonFixtureRequest(
        event: EventModel,
        participantMapping?: ParticipantMapping,
        scoreboardMode?: ScoreboardMode,
        marketGroupItemIds?: string,
        dynamicOfferCategory?: string,
    ): FixtureRequest {
        const evType = event.live ? LeftNavType.Live : LeftNavType.Prematch;
        const type = this.epcotConfig.isEnabled ? LeftNavType.Competition : evType;
        const competitionId = event.league.realCompetitionId ?? event.league.parentLeagueId ?? event.league.id;

        return {
            state: type === LeftNavType.Live ? FixtureState.Live : FixtureState.Latest,
            sportIds: event.sport.id.toString(),
            competitionIds: competitionId.toString(),
            regionIds: type === LeftNavType.Prematch && event.sport.id !== SportConstant.Tennis ? event.region.id.toString() : undefined,
            tournamentIds: type === LeftNavType.Prematch && event.sport.id === SportConstant.Tennis ? event.region.id.toString() : undefined,
            participantMapping: participantMapping || ParticipantMapping.ExcludePlayer,
            scoreboardMode: scoreboardMode || ScoreboardMode.Slim,
            ...(dynamicOfferCategory ? { dynamicOfferCategories: dynamicOfferCategory } : { marketGroupItemIds }),
        };
    }

    private setParams(res: EventCarouselFixtureResponse): void {
        this.skip += res.fixtures.length;

        if (!res.totalCount || this.skip >= res.totalCount) {
            this.callFixtures = false;
        }
    }

    private mapResponse(data: EventCarouselFixtureResponse): EventCarouselItem[] {
        return data.fixtures.map((fixture) => {
            return { event: this.fixtureFactory.create(fixture) };
        });
    }
}
